import React, { Fragment, Suspense, ReactNode, useRef, useState } from 'react';

import {
  getCurrentTime,
  getTokenFromUrl,
  TokenError,
  TokenErrorType,
  validateToken,
} from 'zdocs-web-core/lib/doc-editor';
import { TokenInfo } from 'zdocs-web-core/lib/token';
import BrandProvider from '~/core/components/BrandProvider';
import LoadingIndicator from 'zdocs-web-core/components/LoadingIndicator';

import StandaloneEditor, { StandaloneEditorProps } from './StandaloneEditor';
import ErrorPage, { ErrorPageProps, ErrorPageType } from './ErrorPage';
import logger from 'zdocs-web-core/lib/logging/logger';

const TimeProvider = React.lazy(async () => {
  const currentTime = await getCurrentTime();

  return {
    default: ({
      children,
    }: {
      children: (props: { currentTime: number }) => ReactNode;
    }) => {
      return <Fragment>{children({ currentTime })}</Fragment>;
    },
  };
});

function AppPage({ currentTime }: { currentTime: number }) {
  const tokenInfo = useRef<TokenInfo | null>(null);
  const [errorPage, setErrorPage] = useState<ErrorPageType | null>(null);

  if (!tokenInfo.current) {
    try {
      tokenInfo.current = getTokenFromUrl();
      validateToken(tokenInfo.current, currentTime);

      // At the moment we have no way to refresh the token, when it expires it expires.
      const remainingTime = tokenInfo.current.payload.exp * 1000 - currentTime;
      setTimeout(() => {
        setErrorPage(ErrorPageType.Expired);
      }, remainingTime);
    } catch (e: unknown) {
      let nextErrorPage = ErrorPageType.Invalid;

      if (e instanceof TokenError) {
        switch (e.type) {
          case TokenErrorType.Expired:
            nextErrorPage = ErrorPageType.Expired;
            break;
          case TokenErrorType.Missing:
          case TokenErrorType.Invalid:
            nextErrorPage = ErrorPageType.Invalid;
            break;
          default:
            logger.error('Unknown error occurred', e);
            nextErrorPage = ErrorPageType.Unknown;
        }
      }

      setErrorPage(nextErrorPage);
    }
  }

  function handleOnError() {
    setErrorPage(ErrorPageType.Unknown);
  }

  const errorPageProps: ErrorPageProps = {
    type: errorPage,
    tokenInfo: tokenInfo.current,
  };

  const standAloneEditorProps: StandaloneEditorProps | null = tokenInfo.current
    ? {
        token: tokenInfo.current.token,
        onError: handleOnError,
      }
    : null;

  return (
    <Fragment>
      {errorPageProps.type !== null ? (
        <ErrorPage {...errorPageProps} />
      ) : standAloneEditorProps !== null ? (
        <StandaloneEditor {...standAloneEditorProps} />
      ) : null}
    </Fragment>
  );
}

function App() {
  return (
    <BrandProvider>
      <Suspense fallback={<LoadingIndicator />}>
        <TimeProvider>
          {({ currentTime }) => <AppPage currentTime={currentTime} />}
        </TimeProvider>
      </Suspense>
    </BrandProvider>
  );
}

export default App;
